// Maps the `show_related_resources` tool-call args into row view-models. The call
// is saved with the thread, so live and reopened chats go through the same path.

import { AG_UI_TOOL } from '../agent/ag-ui.constants';
import { parseToolArgs } from '../agent/tool-args';
import { OBJECT_ID } from './doc-answer';
import type { AssistantMessageLike } from './doc-answer';

// The server caps at 5; re-applied so a bad payload can't grow the box.
const RELATED_RESOURCES_LIMIT = 5;

/** One related document row. `title` is null when the server had none. */
export interface RelatedResource {
  key: string;
  documentId: string;
  title: string | null;
}

interface RawRelatedResource {
  document_id?: unknown;
  title?: unknown;
}

// Mid-stream the last id can be truncated; only a full ObjectId is a row.
function hasDocumentId(item: unknown): item is RawRelatedResource & { document_id: string } {
  const documentId = (item as RawRelatedResource | null)?.document_id;
  return typeof documentId === 'string' && OBJECT_ID.test(documentId);
}

function toTitle(value: unknown): string | null {
  const title = typeof value === 'string' ? value.trim() : '';
  return title || null;
}

/** The related documents an assistant message carries, most related first; [] when none. */
export function toRelatedResources(message: AssistantMessageLike): RelatedResource[] {
  const call = (message.toolCalls || []).find(
    toolCall => toolCall.function?.name === AG_UI_TOOL.SHOW_RELATED_RESOURCES,
  );
  if (!call) return [];
  const items = parseToolArgs(call.function.arguments).related_resources;
  if (!Array.isArray(items)) return [];
  return items
    .filter(hasDocumentId)
    .slice(0, RELATED_RESOURCES_LIMIT)
    .map((item, index) => ({
      key: `${call.id}-${index}`,
      documentId: item.document_id,
      title: toTitle(item.title),
    }));
}
